/**
 * 모듈 정보 팝업 조립 — 전투 그리드 탭(battleBoot)·합성/융합 탭(composeBoot)·합성 결과 공용.
 * 인스턴스 정체(이름·모양·풀·기능)를 표시. instanceId가 보유 인벤토리에 없으면(이미 소비/이동) no-op.
 */
import { buildModuleInfoData, buildLauncherInfoData, type LauncherInfoSource } from "./tabData.js";
import { REWARD_ICON_KEY } from "./rewardIcons.js";
import { EconomyController } from "../systems/EconomyController.js";
import { t } from "../i18n/i18n.js";
import type { ModalHost } from "./ModalHost.js";
import type { ModuleInfoAction } from "./ModuleInfoPopup.js";
import type { GameContext } from "../app/GameContext.js";
import type { ModuleGridController } from "../systems/ModuleGridController.js";

/**
 * 전투 그리드 탭 정보 팝업 하단 "장착 해제" 액션 — instanceId가 그리드에 배치(장착)된 경우만 생성.
 * 미배치(팔레트 미장착 타일) 인스턴스는 undefined(버튼 없음 — 정보/강화 전용 유지).
 * onAction: 배치 anchor(placement.x,y) 제거(인벤토리 복귀) 후 팝업 닫기. Phaser-free(순수, 테스트 대상).
 * 강화 버튼(buildUpgradeAction)과 별도 슬롯 — 배치된 파이프는 강화+해제 두 버튼이 공존한다.
 */
export function buildUnequipAction(
  grid: ModuleGridController,
  instanceId: string,
  label: string,
  onRemove: (x: number, y: number) => void,
  onClose: () => void,
): ModuleInfoAction | undefined {
  const placement = grid.placementOf(instanceId);
  if (!placement) return undefined;
  return {
    label,
    enabled: true,
    onAction: () => {
      onRemove(placement.x, placement.y);
      onClose();
    },
  };
}

export interface ModuleInfoModalDeps {
  modals: ModalHost;
  ctx: GameContext;
  screen: () => { width: number; height: number };
  /**
   * 모듈 레벨업 — moduleId(definitionId)의 플레이어 레벨 +1 (강화석 전용). 제공 시 정보 팝업 하단에
   * 강화 버튼을 띄운다(파이프 상세에서 강화 = 명세). 융합 산출물·미제공이면 강화 버튼 없음.
   */
  upgradeModule?: (moduleId: string) => void;
  /**
   * 그리드 배치 제거 — (x,y) 점유 모듈을 인벤토리로 회수. 제공 시 배치(장착)된 인스턴스에 한해
   * "장착 해제" 버튼을 띄운다(전투 그리드 탭 전용 — 미제공이면 해제 버튼 없음). 강화 버튼과 공존.
   */
  removeModuleAt?: (x: number, y: number) => void;
}

export function openModuleInfoModal(
  deps: ModuleInfoModalDeps,
  instanceId: string,
  action?: ModuleInfoAction,
): void {
  const inst = deps.ctx.inventory().getInstance(instanceId);
  if (!inst) return;
  const screen = deps.screen();
  // 강화 버튼 — 외부 action(합성 등록 등)이 없고 upgradeModule이 주입됐으며 융합 산출물이 아닐 때만.
  // 레벨은 moduleId별 플레이어 자산이라 같은 모듈 인스턴스 어디서 열어도 동일 레벨/비용.
  const upgradeAction =
    !action && deps.upgradeModule && !inst.fused
      ? buildUpgradeAction(deps, instanceId, inst.definitionId)
      : action;
  // 장착 해제 — 배치된 인스턴스 + removeModuleAt 주입(전투 탭) 시 강화 버튼과 별개 슬롯으로 공존.
  const unequipAction = deps.removeModuleAt
    ? buildUnequipAction(
        deps.ctx.grid(),
        instanceId,
        t("moduleInfo.unequip"),
        deps.removeModuleAt,
        () => deps.modals.closeModuleInfo(),
      )
    : undefined;
  deps.modals.openModuleInfo({
    screenWidth: screen.width,
    screenHeight: screen.height,
    onClose: () => deps.modals.closeModuleInfo(),
    action: upgradeAction,
    unequipAction,
  });
  deps.modals.updateModuleInfo(
    buildModuleInfoData(
      inst,
      deps.ctx.moduleRegistry(),
      deps.ctx.chapter().bestChapter,
      deps.ctx.playerModuleLevels().level(inst.definitionId),
    ),
  );
}

/**
 * 강화 버튼 action — 라벨에 필요 강화석. 클릭 시 즉시 강화하지 않고 **확인 모달**(UpgradeConfirmPopup)을
 * 띄워 현재→다음 레벨/스탯 변화 + 비용을 보여준다(오클릭 방지). 버튼은 항상 활성(정보 확인 목적) —
 * 실제 강화석 게이트는 확인 모달의 Yes 버튼이 담당. Yes에서만 upgradeModule + 정보 팝업 재오픈.
 */
function buildUpgradeAction(
  deps: ModuleInfoModalDeps,
  instanceId: string,
  moduleId: string,
): ModuleInfoAction {
  const level = deps.ctx.playerModuleLevels().level(moduleId);
  const stoneCost = EconomyController.upgradeStoneCost(level);
  return {
    label: t("moduleInfo.upgradeButton", { stone: stoneCost }),
    enabled: true,
    iconKey: REWARD_ICON_KEY.upgradeStone, // 강화석 아이콘 — 라벨 "영구 강화 N" 왼쪽
    iconSize: 14,
    onAction: () => {
      const inst = deps.ctx.inventory().getInstance(instanceId);
      if (!inst) return;
      const screen = deps.screen();
      const registry = deps.ctx.moduleRegistry();
      const best = deps.ctx.chapter().bestChapter;
      deps.modals.openUpgradeConfirm({
        screenWidth: screen.width,
        screenHeight: screen.height,
        moduleName: t(`module.${moduleId}.name`),
        currentLevel: level,
        nextLevel: level + 1,
        current: buildModuleInfoData(inst, registry, best, level),
        next: buildModuleInfoData(inst, registry, best, level + 1),
        stoneCost,
        ownedStone: deps.ctx.upgradeInv().universalUpgradeStone,
        costIconKey: REWARD_ICON_KEY.upgradeStone,
        canAfford: () => deps.ctx.upgradeInv().universalUpgradeStone >= stoneCost,
        onConfirm: () => {
          deps.upgradeModule?.(moduleId);
          deps.modals.closeUpgradeConfirm();
          deps.modals.closeModuleInfo();
          openModuleInfoModal(deps, instanceId);
        },
        onCancel: () => deps.modals.closeUpgradeConfirm(),
      });
    },
  };
}

/**
 * 런처(그리드 시작점) 정보 팝업 — 시작점 탭 시. 인스턴스 불요(전역 LAUNCHER 설정).
 * spaceshipId가 있으면 우주선별 시작 모듈 이름/설명을 표시한다.
 */
export function openLauncherInfoModal(deps: ModuleInfoModalDeps, source?: LauncherInfoSource): void {
  const screen = deps.screen();
  deps.modals.openModuleInfo({
    screenWidth: screen.width,
    screenHeight: screen.height,
    onClose: () => deps.modals.closeModuleInfo(),
  });
  deps.modals.updateModuleInfo(buildLauncherInfoData(source));
}
